/**
 * RBAC API Service
 * 
 * Handles organizations, accounts, roles, permissions, and access control.
 * Uses cookie-based session management with credentials: 'include'.
 * 
 * @module rbacApi
 */

import { apiConfig } from '@/config/apiConfig';
import type {
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationListResponse,
  TransferOwnershipResponse,
  OrgMember,
  OrgMemberListResponse,
  InviteMemberRequest,
  InviteMemberResponse,
  AcceptInviteRequest,
  AcceptInviteResponse,
  Account,
  CreateAccountRequest,
  UpdateAccountRequest,
  AccountListResponse,
  AccountWithSubAccounts,
  GrantAccountAccessRequest,
  AccountAccessResponse,
  AccountUsersResponse,
  SubAccount,
  CreateSubAccountRequest,
  UpdateSubAccountRequest,
  SubAccountListResponse,
  Role,
  RoleWithPermissions,
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleListResponse,
  SetRolePermissionsResponse,
  Permission,
  PermissionListResponse,
  PermissionsByCategory,
  PermissionCategoriesResponse,
  UserRole,
  UserRoleListResponse,
  AssignRoleRequest,
  AssignRoleByCodeRequest,
  AssignRoleResponse,
  RemoveRoleRequest,
  RemoveRoleResponse,
  UserAccessProfile,
  EffectivePermissions,
  RBACUser,
  GetUserByExternalIdResponse,
  RBACSuccessResponse,
  ScopeType,
  // v2.0 types
  UserContext,
  FullUserContext,
  PermissionCheckRequest,
  PermissionCheckResponse,
  BulkPermissionCheckRequest,
  BulkPermissionCheckResponse,
  RoleConstraints,
  SetupUserOrgRequest,
  SetupUserOrgResponse,
  Group,
  GroupListResponse,
  CreateGroupRequest,
  UpdateGroupRequest,
  GroupMember,
  GroupMemberListResponse,
  AddGroupMemberRequest,
  BulkAddGroupMembersRequest,
  GroupRoleAssignment,
  AssignGroupRoleRequest,
  UserContextGroup,
  ACLEntry,
  ACLListResponse,
  CreateACLRequest,
  UpdateACLRequest,
  ObjectAccessCheckRequest,
  ObjectAccessCheckResponse,
  ApprovalPolicy,
  CreateApprovalPolicyRequest,
  ApprovalRequest,
  ApprovalRequestListResponse,
  ApprovalRequestWithDecisions,
  CreateApprovalRequestRequest,
  ApprovalDecisionRequest,
  ApprovalCheckRequest,
  ApprovalCheckResponse,
  RecentCampaignsResponse,
  WebAnalyticsResponse,
  LeadPathResponse,
  AuditLogEntry,
  AuditLogQuery,
  AuditLogResponse,
  AccountTree,
} from '@/types/rbac';

const BASE_URL = apiConfig.baseUrls.rbac;

/**
 * Custom error class for RBAC API errors
 */
export class RBACApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string,
    public data?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'RBACApiError';
  }
}

/**
 * Base fetch function with credentials and error handling
 */
async function fetchWithCredentials<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 401) {
    throw new RBACApiError('Session expired', 401, 'Unauthorized');
  }

  if (response.status === 403) {
    throw new RBACApiError('Access denied', 403, 'Forbidden');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: 'Request failed' }));
    const detail = body.detail;
    let message = 'Request failed';

    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = (detail[0]?.msg || detail[0]?.error || 'Validation error') as string;
    } else if (detail?.error) {
      message = detail.error;
    } else if (body.error) {
      message = body.error;
    } else if (body.message) {
      message = body.message;
    }

    throw new RBACApiError(message, response.status, typeof detail === 'string' ? detail : undefined, body);
  }

  return response.json();
}

// ============================================
// Organizations
// ============================================

/**
 * Create a new organization
 */
export async function createOrganization(
  data: CreateOrganizationRequest
): Promise<Organization> {
  return fetchWithCredentials<Organization>('/api/orgs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get current user's organizations
 */
export async function getMyOrganizations(): Promise<OrganizationListResponse> {
  return fetchWithCredentials<OrganizationListResponse>('/api/orgs/my', {
    method: 'GET',
  });
}

/**
 * Get organization by ID.
 * API returns { success, organization }; we unwrap to return the inner organization.
 */
export async function getOrganization(orgId: number): Promise<Organization> {
  const data = await fetchWithCredentials<{ success?: boolean; organization?: Organization }>(`/api/orgs/${orgId}`, {
    method: 'GET',
  });
  return (data?.organization ?? data) as Organization;
}

/**
 * Update organization
 */
export async function updateOrganization(
  orgId: number,
  data: UpdateOrganizationRequest
): Promise<Organization> {
  return fetchWithCredentials<Organization>(`/api/orgs/${orgId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete organization
 */
export async function deleteOrganization(orgId: number): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/orgs/${orgId}`, {
    method: 'DELETE',
  });
}

/**
 * Transfer organization ownership
 */
export async function transferOwnership(
  orgId: number,
  newOwnerUserId: number
): Promise<TransferOwnershipResponse> {
  return fetchWithCredentials<TransferOwnershipResponse>(`/api/orgs/${orgId}/transfer`, {
    method: 'POST',
    body: JSON.stringify({ new_owner_user_id: newOwnerUserId }),
  });
}

/**
 * Get organization members
 */
export async function getOrganizationMembers(orgId: number): Promise<OrgMemberListResponse> {
  return fetchWithCredentials<OrgMemberListResponse>(`/api/orgs/${orgId}/members`, {
    method: 'GET',
  });
}

/**
 * Invite member to organization
 */
export async function inviteMember(
  orgId: number,
  data: InviteMemberRequest
): Promise<InviteMemberResponse> {
  return fetchWithCredentials<InviteMemberResponse>(`/api/orgs/${orgId}/members`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Remove a member from the organization (RBAC internal user id).
 */
export async function removeOrganizationMember(
  orgId: number,
  memberRbacUserId: number
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(
    `/api/orgs/${orgId}/members/${memberRbacUserId}`,
    { method: 'DELETE' }
  );
}

/**
 * Update org member status: active, suspended, invited, removed (RBAC internal user id).
 */
export async function updateOrganizationMemberStatus(
  orgId: number,
  memberRbacUserId: number,
  status: 'active' | 'suspended' | 'invited' | 'removed'
): Promise<{ success: boolean; member?: unknown; error?: string }> {
  return fetchWithCredentials(`/api/orgs/${orgId}/members/${memberRbacUserId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

/**
 * Get invite details by token (public, no auth required)
 */
export async function getInviteDetails(
  token: string
): Promise<{ success: boolean; email?: string; org_name?: string; org_id?: number; account_name?: string; expires_at?: string; error?: string }> {
  return fetchWithCredentials(`/api/orgs/invites/details?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  });
}

/**
 * Accept organization invite
 */
export async function acceptInvite(
  inviteToken: string
): Promise<AcceptInviteResponse> {
  const body: AcceptInviteRequest = { invite_token: inviteToken };
  return fetchWithCredentials<AcceptInviteResponse>('/api/orgs/invites/accept', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================
// Accounts
// ============================================

/**
 * Create a new account
 */
export async function createAccount(data: CreateAccountRequest): Promise<Account> {
  return fetchWithCredentials<Account>('/api/accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * List accounts for organization
 */
export async function listAccounts(
  orgId: number,
  options?: { status?: string; limit?: number; offset?: number }
): Promise<AccountListResponse> {
  // Backend expects org_id, plus optional status, skip, limit
  const params = new URLSearchParams({ org_id: String(orgId) });
  if (options?.status) params.append('status', options.status);
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.offset) params.append('skip', String(options.offset));
  
  const raw = await fetchWithCredentials<AccountListResponse>(`/api/accounts?${params.toString()}`, {
    method: 'GET',
  });

  // Normalize { items } -> { accounts }
  const accounts = raw.items || raw.accounts || [];
  const total = raw.total ?? accounts.length;

  return { accounts, total };
}

// Coalesces callers requesting the same (orgId, options) within a short window — mirrors
// fetchSubTenantMemberUserIds's dedup in subTenantMemberUserIds.ts. At least 7 independent
// Axios providers (WorkflowAxios, PredictionAxios, DigitalAdAxios, DirectMailAxios,
// AudienceBuilderAxios, QRCodeAxios, EmailAxios) plus SubAccountSelector each call
// listAllAccounts on mount — without this, that's 7+ near-simultaneous, identical
// GET /api/accounts?org_id=...&limit=100 requests on every single page load.
const LIST_ALL_ACCOUNTS_DEDUP_GRACE_MS = 3000;
const listAllAccountsInFlight = new Map<string, Promise<Account[]>>();

function listAllAccountsCacheKey(orgId: number, options?: { status?: string; pageSize?: number }): string {
  return `${orgId}:${options?.status ?? ''}:${options?.pageSize ?? ''}`;
}

/**
 * List every account in an org (follows pagination; single-page listAccounts can omit tenants).
 */
export async function listAllAccounts(
  orgId: number,
  options?: { status?: string; pageSize?: number }
): Promise<Account[]> {
  const key = listAllAccountsCacheKey(orgId, options);
  const existing = listAllAccountsInFlight.get(key);
  if (existing) {
    return [...(await existing)];
  }

  const promise = doListAllAccounts(orgId, options);
  listAllAccountsInFlight.set(key, promise);
  promise.then(
    () => {
      // Keep the settled result shareable briefly so staggered (not exactly concurrent)
      // callers for the same org still get one shared request instead of firing their own.
      setTimeout(() => {
        if (listAllAccountsInFlight.get(key) === promise) listAllAccountsInFlight.delete(key);
      }, LIST_ALL_ACCOUNTS_DEDUP_GRACE_MS);
    },
    () => {
      // Don't replay a failure to other callers — let the next one retry immediately.
      if (listAllAccountsInFlight.get(key) === promise) listAllAccountsInFlight.delete(key);
    }
  );
  return [...(await promise)];
}

async function doListAllAccounts(
  orgId: number,
  options?: { status?: string; pageSize?: number }
): Promise<Account[]> {
  const pageSize = options?.pageSize ?? 100;
  const seen = new Set<number>();
  const all: Account[] = [];
  let offset = 0;
  for (;;) {
    const response = await listAccounts(orgId, {
      status: options?.status,
      limit: pageSize,
      offset,
    });
    const batch = response.accounts ?? [];
    if (batch.length === 0) break;
    for (const acc of batch) {
      if (!seen.has(acc.id)) {
        seen.add(acc.id);
        all.push(acc);
      }
    }
    if (batch.length < pageSize) break;
    offset += batch.length;
  }
  return all;
}

/**
 * Get account by ID (optionally with sub-accounts)
 */
export async function getAccount(
  accountId: number,
  includeSubAccounts?: boolean
): Promise<AccountWithSubAccounts> {
  const path = includeSubAccounts
    ? `/api/accounts/${accountId}/full`
    : `/api/accounts/${accountId}`;
  return fetchWithCredentials<AccountWithSubAccounts>(path, {
    method: 'GET',
  });
}

/**
 * Update account
 */
export async function updateAccount(
  accountId: number,
  data: UpdateAccountRequest
): Promise<Account> {
  return fetchWithCredentials<Account>(`/api/accounts/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Assign a role to an account
 */
export async function assignRoleToAccount(
  accountId: number,
  roleId?: number,
  roleCode?: string,
  isDefault: boolean = true
): Promise<{ success: boolean; account: Account; message?: string }> {
  return fetchWithCredentials(`/api/accounts/${accountId}/roles`, {
    method: 'POST',
    body: JSON.stringify({
      role_id: roleId,
      role_code: roleCode,
      is_default: isDefault,
    }),
  });
}

/** Remove a role from an account's allowed catalog (cannot remove the last role). */
export async function removeRoleFromAccount(
  accountId: number,
  roleId: number
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/accounts/${accountId}/roles/${roleId}`, {
    method: 'DELETE',
  });
}

/**
 * Delete account
 */
export async function deleteAccount(accountId: number): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/accounts/${accountId}`, {
    method: 'DELETE',
  });
}

/**
 * Grant account access to user
 */
export async function grantAccountAccess(
  accountId: number,
  data: GrantAccountAccessRequest
): Promise<AccountAccessResponse> {
  return fetchWithCredentials<AccountAccessResponse>(`/api/accounts/${accountId}/access`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Revoke account access from user
 */
export async function revokeAccountAccess(
  accountId: number,
  userId: number
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/accounts/${accountId}/access/${userId}`, {
    method: 'DELETE',
  });
}

/**
 * List users with access to an account.
 * For sub-tenant scoping: `accountId` = sub-tenant account id (path); `tenantId` = organization id (`?tenantid=` / org_id).
 */
export async function listAccountUsers(
  accountId: number,
  includeSubAccounts: boolean = true,
  tenantId?: number,
  registeredOnly?: boolean
): Promise<AccountUsersResponse> {
  const params = new URLSearchParams();
  if (includeSubAccounts) params.append('includesubaccounts', 'true');
  if (tenantId) params.append('tenantid', String(tenantId));
  // Excludes invited-but-not-yet-registered placeholder users (negative external_user_id) —
  // use for member pickers; omit for admin views that manage/display pending invites too.
  if (registeredOnly) params.append('registered_only', 'true');

  return fetchWithCredentials<AccountUsersResponse>(
    `/api/accounts/${accountId}/users?${params.toString()}`,
    { method: 'GET' }
  );
}

/**
 * Normalize account roles API response so we always get an array of Role.
 * Handles: { roles }, { data.roles }, { account_roles: [{ role }] }, { role } (single).
 */
function normalizeAccountRolesResponse(raw: any): { roles: Role[]; total: number } {
  if (!raw || typeof raw !== 'object') {
    return { roles: [], total: 0 };
  }
  let list: any[] = [];
  if (Array.isArray(raw.roles)) {
    list = raw.roles;
  } else if (Array.isArray(raw.data?.roles)) {
    list = raw.data.roles;
  } else if (Array.isArray(raw.account_roles)) {
    list = raw.account_roles.map((a: any) => a.role ?? a).filter(Boolean);
  } else if (raw.role && typeof raw.role === 'object') {
    list = [raw.role];
  } else if (raw.roles && typeof raw.roles === 'object' && !Array.isArray(raw.roles)) {
    list = [raw.roles];
  }
  const roles = list
    .filter((r) => r && (r.id != null || r.code != null))
    .map((r) => ({
      id: r.id,
      code: r.code ?? '',
      name: r.name ?? r.role_name ?? '',
      description: r.description ?? null,
      organization_id: r.organization_id ?? null,
      is_system: !!r.is_system,
      is_default: !!r.is_default,
      priority: r.priority ?? 0,
      created_at: r.created_at ?? '',
      updated_at: r.updated_at ?? null,
    })) as Role[];
  const total = raw.total ?? raw.data?.total ?? roles.length;
  return { roles, total };
}

/**
 * Get roles allowed for an account (all assigned roles for that account).
 */
export async function getAccountRoles(
  accountId: number
): Promise<{ roles: Role[]; total: number }> {
  const raw = await fetchWithCredentials<any>(
    `/api/accounts/${accountId}/roles`,
    { method: 'GET' }
  );
  return normalizeAccountRolesResponse(raw);
}

/**
 * Get roles allowed for a sub-account
 */
export async function getSubAccountRoles(
  subAccountId: number
): Promise<{ roles: Role[]; total: number }> {
  return fetchWithCredentials<{ roles: Role[]; total: number }>(
    `/api/sub-accounts/${subAccountId}/roles`,
    { method: 'GET' }
  );
}

/**
 * Assign a role to a sub-account
 */
export async function assignRoleToSubAccount(
  subAccountId: number,
  data: { role_id?: number; role_code?: string; is_default?: boolean }
): Promise<{ success: boolean; sub_account: any; message?: string }> {
  return fetchWithCredentials<{ success: boolean; sub_account: any; message?: string }>(
    `/api/sub-accounts/${subAccountId}/roles`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/** Remove a role from a sub-account's allowed catalog (cannot remove the last role). */
export async function removeRoleFromSubAccount(
  subAccountId: number,
  roleId: number
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/sub-accounts/${subAccountId}/roles/${roleId}`, {
    method: 'DELETE',
  });
}

// ============================================
// Sub-Accounts
// ============================================

/**
 * Create a sub-account
 */
export async function createSubAccount(
  accountId: number,
  data: CreateSubAccountRequest
): Promise<SubAccount> {
  return fetchWithCredentials<SubAccount>(`/api/accounts/${accountId}/sub-accounts`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * List sub-accounts
 */
export async function listSubAccounts(accountId: number): Promise<SubAccountListResponse> {
  const raw = await fetchWithCredentials<SubAccountListResponse>(
    `/api/accounts/${accountId}/sub-accounts`,
    { method: 'GET' }
  );

  const sub_accounts = raw.items || raw.sub_accounts || [];
  const total = raw.total ?? sub_accounts.length;

  return { sub_accounts, total };
}

/**
 * Get sub-account by ID
 */
export async function getSubAccount(subAccountId: number): Promise<SubAccount> {
  return fetchWithCredentials<SubAccount>(`/api/sub-accounts/${subAccountId}`, {
    method: 'GET',
  });
}

/**
 * Update sub-account
 */
export async function updateSubAccount(
  subAccountId: number,
  data: UpdateSubAccountRequest
): Promise<SubAccount> {
  return fetchWithCredentials<SubAccount>(`/api/sub-accounts/${subAccountId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete sub-account
 */
export async function deleteSubAccount(subAccountId: number): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/sub-accounts/${subAccountId}`, {
    method: 'DELETE',
  });
}

/**
 * Grant sub-account access to user
 */
export async function grantSubAccountAccess(
  subAccountId: number,
  data: GrantAccountAccessRequest
): Promise<AccountAccessResponse> {
  return fetchWithCredentials<AccountAccessResponse>(`/api/sub-accounts/${subAccountId}/access`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Revoke sub-account access from user
 */
export async function revokeSubAccountAccess(
  subAccountId: number,
  userId: number
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/sub-accounts/${subAccountId}/access/${userId}`, {
    method: 'DELETE',
  });
}

// ============================================
// Roles
// ============================================

/**
 * Get system roles
 */
export async function getSystemRoles(): Promise<RoleListResponse> {
  return fetchWithCredentials<RoleListResponse>('/api/roles/system', {
    method: 'GET',
  });
}

/**
 * List all roles
 */
export async function listRoles(
  options?: { orgId?: number; includeSystem?: boolean }
): Promise<RoleListResponse> {
  const params = new URLSearchParams();
  if (options?.orgId) params.append('org_id', String(options.orgId));
  if (options?.includeSystem !== undefined) {
    params.append('include_system', String(options.includeSystem));
  }
  
  const query = params.toString() ? `?${params}` : '';
  return fetchWithCredentials<RoleListResponse>(`/api/roles${query}`, {
    method: 'GET',
  });
}

/**
 * Create custom role.
 * Backend returns { success, role, message }; unwrap so callers get a Role with id.
 */
export async function createRole(data: CreateRoleRequest): Promise<Role> {
  const raw = await fetchWithCredentials<{
    success?: boolean;
    role?: Role;
    message?: string;
  }>('/api/roles', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  const role = (raw?.role ?? raw) as Role;
  if (!role || role.id == null) {
    throw new RBACApiError('Invalid create role response', 500, 'Invalid response', raw);
  }
  return role;
}

/**
 * Get role by ID (optionally with permissions)
 */
export async function getRole(
  roleId: number,
  includePermissions?: boolean
): Promise<RoleWithPermissions> {
  const params = includePermissions ? '?include_permissions=true' : '';
  return fetchWithCredentials<RoleWithPermissions>(`/api/roles/${roleId}${params}`, {
    method: 'GET',
  });
}

/**
 * Get role by ID with full permissions data
 */
export async function getRoleFull(roleId: number): Promise<RoleWithPermissions> {
  return fetchWithCredentials<RoleWithPermissions>(`/api/roles/${roleId}/full`, {
    method: 'GET',
  });
}

/**
 * Update role.
 * Backend returns { success, role }; unwrap so callers get a Role.
 */
export async function updateRole(roleId: number, data: UpdateRoleRequest): Promise<Role> {
  const raw = await fetchWithCredentials<{
    success?: boolean;
    role?: Role;
    error?: string;
  }>(`/api/roles/${roleId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  const role = (raw?.role ?? raw) as Role;
  if (!role || role.id == null) {
    throw new RBACApiError(raw?.error || 'Invalid update role response', 500, 'Invalid response', raw as Record<string, unknown>);
  }
  return role;
}

/**
 * Delete custom role
 */
export async function deleteRole(roleId: number): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/roles/${roleId}`, {
    method: 'DELETE',
  });
}

/**
 * Set role permissions (replace all)
 */
export async function setRolePermissions(
  roleId: number,
  permissionIds: number[]
): Promise<SetRolePermissionsResponse> {
  return fetchWithCredentials<SetRolePermissionsResponse>(`/api/roles/${roleId}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ permission_ids: permissionIds }),
  });
}

/**
 * Add permission to role
 */
export async function addPermissionToRole(
  roleId: number,
  permissionId: number
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/roles/${roleId}/permissions`, {
    method: 'POST',
    body: JSON.stringify({ permission_id: permissionId }),
  });
}

// ============================================
// Permissions
// ============================================

/**
 * List all permissions
 */
export async function listPermissions(): Promise<PermissionListResponse> {
  return fetchWithCredentials<PermissionListResponse>('/api/permissions', {
    method: 'GET',
  });
}

/**
 * Get permissions grouped by category
 */
export async function getPermissionsGrouped(): Promise<PermissionsByCategory> {
  return fetchWithCredentials<PermissionsByCategory>('/api/permissions/grouped', {
    method: 'GET',
  });
}

/**
 * Get permission categories list
 */
export async function getPermissionCategories(): Promise<PermissionCategoriesResponse> {
  return fetchWithCredentials<PermissionCategoriesResponse>('/api/permissions/categories', {
    method: 'GET',
  });
}

// ============================================
// Access Control
// ============================================

/**
 * Get current user's access profile
 */
export async function getUserAccessProfile(): Promise<UserAccessProfile> {
  return fetchWithCredentials<UserAccessProfile>('/api/access/user/me', {
    method: 'GET',
  });
}

/**
 * Get user's effective permissions in scope
 */
export async function getUserPermissions(
  userId: number,
  scopeType: ScopeType,
  scopeId: number
): Promise<EffectivePermissions> {
  return fetchWithCredentials<EffectivePermissions>(
    `/api/access/user/${userId}/permissions?scope_type=${scopeType}&scope_id=${scopeId}`,
    { method: 'GET' }
  );
}

/**
 * Get effective permissions for a user across multiple scopes in one request.
 */
export async function getUserPermissionsBatch(
  userId: number,
  scopes: Array<{ scope_type: ScopeType; scope_id: number }>
): Promise<{ user_id: number; results: Array<{ scope_type: string; scope_id: number; roles: string[]; permissions: string[] }> }> {
  return fetchWithCredentials(
    `/api/access/user/${userId}/permissions/batch`,
    { method: 'POST', body: JSON.stringify({ scopes }) }
  );
}

/**
 * Get user's roles
 */
export async function getUserRoles(
  userId: number,
  scopeType?: ScopeType,
  scopeId?: number
): Promise<UserRoleListResponse> {
  const params = new URLSearchParams();
  if (scopeType) params.append('scope_type', scopeType);
  if (scopeId) params.append('scope_id', String(scopeId));
  
  const query = params.toString() ? `?${params}` : '';
  return fetchWithCredentials<UserRoleListResponse>(`/api/access/users/${userId}/roles${query}`, {
    method: 'GET',
  });
}

/**
 * Assign role to user
 */
export async function assignRole(
  userId: number,
  data: AssignRoleRequest
): Promise<AssignRoleResponse> {
  return fetchWithCredentials<AssignRoleResponse>(`/api/access/users/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Assign role to user by role code
 */
export async function assignRoleByCode(
  userId: number,
  data: AssignRoleByCodeRequest
): Promise<AssignRoleResponse> {
  return fetchWithCredentials<AssignRoleResponse>(`/api/access/users/${userId}/roles/by-code`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Remove role from user
 */
export async function removeRole(
  userId: number,
  data: RemoveRoleRequest
): Promise<RemoveRoleResponse> {
  return fetchWithCredentials<RemoveRoleResponse>(`/api/access/users/${userId}/roles`, {
    method: 'DELETE',
    body: JSON.stringify(data),
  });
}

// ============================================
// Direct (per-user) permission grants
// ============================================

export interface DirectPermissionGrantRequest {
  permission_codes: string[];
  scope_type: ScopeType;
  scope_id: number;
}

/**
 * Grant permissions straight to a user (merges with existing grants at that scope)
 */
export async function grantDirectPermissions(
  userId: number,
  data: DirectPermissionGrantRequest
): Promise<unknown> {
  return fetchWithCredentials(`/api/access/users/${userId}/permissions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Revoke directly-granted permissions from a user
 */
export async function revokeDirectPermissions(
  userId: number,
  data: DirectPermissionGrantRequest
): Promise<unknown> {
  return fetchWithCredentials(`/api/access/users/${userId}/permissions`, {
    method: 'DELETE',
    body: JSON.stringify(data),
  });
}

/**
 * List a user's directly-granted permissions at a scope (admin)
 */
export async function getDirectPermissions(
  userId: number,
  scopeType: ScopeType,
  scopeId: number
): Promise<unknown> {
  return fetchWithCredentials(
    `/api/access/users/${userId}/permissions/direct?scope_type=${scopeType}&scope_id=${scopeId}`,
    { method: 'GET' }
  );
}

// ============================================
// User Management
// ============================================

/**
 * Get user by external ID (from user-svc)
 */
export async function getUserByExternalId(
  externalId: number
): Promise<GetUserByExternalIdResponse> {
  return fetchWithCredentials<GetUserByExternalIdResponse>(
    `/api/access/users/external/${externalId}`,
    { method: 'GET' }
  );
}

/**
 * Get user by RBAC ID
 */
export async function getUser(userId: number): Promise<RBACUser> {
  const response = await fetchWithCredentials<any>(`/api/access/users/${userId}`, {
    method: 'GET',
  });
  
  // Handle wrapped response: { success: true, user: {...} }
  if (response.user) {
    return response.user;
  }
  
  // Handle direct response
  return response;
}

// ============================================
// RBAC v2.0 - User Setup (First Login)
// ============================================

/**
 * Setup user and personal organization on first login
 * This is the RECOMMENDED way to onboard new users
 */
export async function setupUserOrg(
  data: SetupUserOrgRequest
): Promise<SetupUserOrgResponse> {
  return fetchWithCredentials<SetupUserOrgResponse>('/api/access/setup/user-org', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// RBAC v2.0 - User Context (Key for UI Gating)
// ============================================

/**
 * Map GET /api/v1/me/context response to the shape RBACProvider expects.
 * Backend uses org_id, account object, constraints, and group objects; frontend used organization_id, etc.
 */
function normalizeMeContext(raw: Record<string, unknown>): UserContext {
  const permissions = Array.isArray(raw.permissions) ? (raw.permissions as string[]) : [];
  const roles = (Array.isArray(raw.roles) ? raw.roles : []) as UserContext['roles'];

  const groupsRaw = raw.groups;
  const groups: string[] = Array.isArray(groupsRaw)
    ? groupsRaw
        .map((g: unknown) => {
          if (typeof g === 'string') return g;
          if (g && typeof g === 'object' && 'group_name' in g) {
            return String((g as { group_name: string }).group_name);
          }
          return '';
        })
        .filter(Boolean)
    : [];

  const approval = raw.approval_policy as Record<string, unknown> | undefined;
  const approval_policy = {
    requires_approval_for_publish: Boolean(
      approval?.requires_approval_for_publish ?? approval?.publish_requires_approval
    ),
    requires_approval_for_spend: Boolean(
      approval?.requires_approval_for_spend ??
        (approval?.spend_threshold_approval != null && approval?.spend_threshold_approval !== false)
    ),
    spend_threshold: 0,
  };
  const st = approval?.spend_threshold_approval;
  if (st && typeof st === 'object' && st !== null && 'threshold' in st) {
    approval_policy.spend_threshold = Number((st as { threshold?: number }).threshold ?? 0);
  } else if (typeof approval?.spend_threshold === 'number') {
    approval_policy.spend_threshold = approval.spend_threshold;
  }

  const account = raw.account as { account_id?: number; id?: number; parent_account_id?: number | null } | null | undefined;
  const account_id = Number(
    raw.account_id ?? account?.account_id ?? account?.id ?? 0
  );
  const parent_account_id = account?.parent_account_id ?? null;

  let entitlements: string[] = [];
  const ent = raw.entitlements;
  if (Array.isArray(ent)) {
    entitlements = ent.filter((x): x is string => typeof x === 'string');
  } else if (ent && typeof ent === 'object' && 'features' in ent && Array.isArray((ent as { features: string[] }).features)) {
    entitlements = (ent as { features: string[] }).features;
  }

  return {
    user_id: Number(raw.user_id ?? 0),
    account_id,
    organization_id: Number(raw.organization_id ?? raw.org_id ?? 0),
    owner_user_id: Number(raw.owner_user_id ?? 0) || undefined,
    is_org_owner: Boolean(raw.is_org_owner),
    parent_account_id,
    permissions,
    roles,
    groups,
    aggregated_constraints: (raw.aggregated_constraints ?? raw.constraints ?? {}) as UserContext['aggregated_constraints'],
    approval_policy,
    entitlements,
  };
}

/**
 * Get user context for UI gating (simple version)
 * Returns permissions, roles, groups, constraints, and approval policy
 */
export async function getUserContext(accountId?: number): Promise<UserContext> {
  const params = accountId ? `?account_id=${accountId}` : '';
  const raw = await fetchWithCredentials<Record<string, unknown>>(`/api/v1/me/context${params}`, {
    method: 'GET',
  });
  return normalizeMeContext(raw);
}

/**
 * Get full user context for UI gating
 * Returns complete context including org info, account info, etc.
 */
export async function getFullUserContext(accountId?: number): Promise<FullUserContext> {
  const params = accountId ? `?account_id=${accountId}` : '';
  const raw = await fetchWithCredentials<Record<string, unknown>>(`/api/v1/me/context${params}`, {
    method: 'GET',
  });
  const normalized = normalizeMeContext(raw);
  return {
    ...normalized,
    success: true,
    org_id: normalized.organization_id,
    owner_user_id: normalized.owner_user_id,
    has_support_access: Boolean(raw.has_support_access),
    is_org_owner: normalized.is_org_owner ?? Boolean(raw.is_org_owner),
    is_account_admin: Boolean(raw.is_account_admin),
    email: String(raw.email ?? ''),
    display_name: String(raw.display_name ?? ''),
    org_name: String(raw.org_name ?? ''),
    account: (raw.account as FullUserContext['account']) ?? null,
    constraints: normalized.aggregated_constraints,
    approval_policy: normalized.approval_policy as unknown as FullUserContext['approval_policy'],
    groups: (raw.groups as UserContextGroup[]) || [],
  } as FullUserContext;
}

/**
 * Get just permissions for user in scope
 */
export async function getMyPermissions(accountId?: number): Promise<{ permissions: string[] }> {
  const params = accountId ? `?account_id=${accountId}` : '';
  return fetchWithCredentials<{ permissions: string[] }>(`/api/v1/me/permissions${params}`, {
    method: 'GET',
  });
}

/**
 * Check if user has specific permission (server-side)
 */
export async function checkPermission(
  data: PermissionCheckRequest
): Promise<PermissionCheckResponse> {
  return fetchWithCredentials<PermissionCheckResponse>('/api/access/check', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Check multiple permissions at once
 */
export async function checkPermissionBulk(
  data: BulkPermissionCheckRequest
): Promise<BulkPermissionCheckResponse> {
  return fetchWithCredentials<BulkPermissionCheckResponse>('/api/access/check/bulk', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Check if an action requires approval
 */
export async function checkApprovalRequired(
  data: ApprovalCheckRequest
): Promise<ApprovalCheckResponse> {
  return fetchWithCredentials<ApprovalCheckResponse>('/api/approvals/check', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// RBAC v2.0 - Account Tree
// ============================================

/**
 * Get account tree for organization
 */
export async function getAccountTree(orgId: number): Promise<AccountTree> {
  return fetchWithCredentials<AccountTree>(`/api/accounts/tree?org_id=${orgId}`, {
    method: 'GET',
  });
}

// ============================================
// RBAC v2.0 - Role Constraints
// ============================================

/**
 * Set constraints on a role assignment
 */
export async function setRoleConstraints(
  roleId: number,
  constraints: RoleConstraints
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/roles/${roleId}/constraints`, {
    method: 'PUT',
    body: JSON.stringify(constraints),
  });
}

// ============================================
// RBAC v2.0 - Groups/Teams
// ============================================

/**
 * List groups for an organization / tenant
 *
 * New RBAC API expects a tenant_id query parameter rather than org_id.
 */
export async function listGroups(
  orgId: number,
  options?: { limit?: number; offset?: number }
): Promise<GroupListResponse> {
  const params = new URLSearchParams({
    tenant_id: String(orgId),
  });
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.offset) params.append('offset', String(options.offset));
  
  const response = await fetchWithCredentials<GroupListResponse>(`/api/groups?${params.toString()}`, {
    method: 'GET',
  });
  
  // Normalize response: handle both 'items' and 'groups' formats
  if (response.items && !response.groups) {
    return {
      groups: response.items,
      total: response.total || response.items.length,
    };
  }
  
  return response;
}

/**
 * Create a new group
 */
export async function createGroup(
  orgId: number,
  data: CreateGroupRequest
): Promise<Group> {
  // Some RBAC deployments expect a tenant_id query param while others use org_id
  // To support both, we send tenant_id in the query string and organization_id in the body.
  const params = new URLSearchParams({
    tenant_id: String(orgId),
  });

  return fetchWithCredentials<Group>(`/api/groups?${params.toString()}`, {
    method: 'POST',
    body: JSON.stringify({ ...data, organization_id: orgId }),
  });
}

/**
 * Get group by ID
 */
export async function getGroup(groupId: number): Promise<Group> {
  return fetchWithCredentials<Group>(`/api/groups/${groupId}`, {
    method: 'GET',
  });
}

/**
 * Update group
 */
export async function updateGroup(
  groupId: number,
  data: UpdateGroupRequest
): Promise<Group> {
  return fetchWithCredentials<Group>(`/api/groups/${groupId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete group
 */
export async function deleteGroup(groupId: number): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/groups/${groupId}`, {
    method: 'DELETE',
  });
}

/**
 * Get group members
 */
export async function getGroupMembers(groupId: number): Promise<GroupMemberListResponse> {
  return fetchWithCredentials<GroupMemberListResponse>(`/api/groups/${groupId}/members`, {
    method: 'GET',
  });
}

/**
 * Add member to group
 */
export async function addGroupMember(
  groupId: number,
  data: AddGroupMemberRequest
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Bulk add members to group
 */
export async function bulkAddGroupMembers(
  groupId: number,
  data: BulkAddGroupMembersRequest
): Promise<{ success: boolean; added_count: number }> {
  return fetchWithCredentials<{ success: boolean; added_count: number }>(
    `/api/groups/${groupId}/members/bulk`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Remove member from group
 */
export async function removeGroupMember(
  groupId: number,
  memberUserId: number
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(
    `/api/groups/${groupId}/members/${memberUserId}`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Get group role assignments
 */
export async function getGroupRoles(groupId: number): Promise<{ roles: GroupRoleAssignment[] }> {
  return fetchWithCredentials<{ roles: GroupRoleAssignment[] }>(`/api/groups/${groupId}/roles`, {
    method: 'GET',
  });
}

/**
 * Assign role to group
 */
export async function assignGroupRole(
  groupId: number,
  data: AssignGroupRoleRequest
): Promise<GroupRoleAssignment> {
  return fetchWithCredentials<GroupRoleAssignment>(`/api/groups/${groupId}/roles`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Remove role from group
 */
export async function removeGroupRole(
  groupId: number,
  roleId: number,
  accountId?: number
): Promise<RBACSuccessResponse> {
  const params = accountId ? `?accountid=${accountId}` : '';
  return fetchWithCredentials<RBACSuccessResponse>(`/api/groups/${groupId}/roles/${roleId}${params}`, {
    method: 'DELETE',
  });
}

// ============================================
// RBAC v2.0 - Object ACL
// ============================================

/**
 * List ACL entries for an object
 */
export async function listACL(
  objectType: string,
  objectId: string
): Promise<ACLListResponse> {
  const params = new URLSearchParams({ object_type: objectType, object_id: objectId });
  return fetchWithCredentials<ACLListResponse>(`/api/acl?${params}`, {
    method: 'GET',
  });
}

/**
 * Create ACL entry
 */
export async function createACL(data: CreateACLRequest): Promise<ACLEntry> {
  return fetchWithCredentials<ACLEntry>('/api/acl', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update ACL entry
 */
export async function updateACL(
  aclId: number,
  data: UpdateACLRequest
): Promise<ACLEntry> {
  return fetchWithCredentials<ACLEntry>(`/api/acl/${aclId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete ACL entry
 */
export async function deleteACL(aclId: number): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/acl/${aclId}`, {
    method: 'DELETE',
  });
}

/**
 * Check if user has access to an object
 */
export async function checkObjectAccess(
  data: ObjectAccessCheckRequest
): Promise<ObjectAccessCheckResponse> {
  return fetchWithCredentials<ObjectAccessCheckResponse>('/api/acl/check', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// RBAC v2.0 - Approvals
// ============================================

/**
 * Get approval policy for account
 *
 * New approvals API:
 *   GET /api/approvals/policies?accountid=...
 *   -> { policyconfig: { ... }, policy_id, account_id }
 *
 * We adapt the response into our existing ApprovalPolicy shape so the rest of
 * the UI can keep using the same type.
 */
export async function getApprovalPolicy(accountId: number): Promise<ApprovalPolicy> {
  const params = new URLSearchParams({ account_id: String(accountId) });

  const raw = await fetchWithCredentials<any>(`/api/approvals/policies?${params.toString()}`, {
    method: 'GET',
  });

  const cfg = raw?.policyconfig || {};

  const policy: ApprovalPolicy = {
    id: raw.policy_id ?? raw.id ?? 0,
    account_id: raw.account_id ?? accountId,
    requires_approval_for_publish: cfg.requires_approval_for_publish ?? false,
    requires_approval_for_spend: cfg.requires_approval_for_spend ?? false,
    spend_threshold: cfg.spend_threshold ?? 0,
    min_approvers: cfg.min_approvers ?? 1,
    auto_approve_after_hours: cfg.auto_approve_after_hours ?? null,
    created_at: raw.created_at ?? new Date().toISOString(),
    updated_at: raw.updated_at ?? null,
  };

  return policy;
}

/**
 * Create approval policy
 */
export async function createApprovalPolicy(
  accountId: number,
  data: CreateApprovalPolicyRequest
): Promise<ApprovalPolicy> {
  const params = new URLSearchParams({ account_id: String(accountId) });

  const raw = await fetchWithCredentials<any>(`/api/approvals/policies?${params.toString()}`, {
    method: 'POST',
    body: JSON.stringify({ policyconfig: data }),
  });

  // Re-use the mapper from getApprovalPolicy but seed it from the request data
  const cfg = raw?.policyconfig || data || {};

  const policy: ApprovalPolicy = {
    id: raw.policy_id ?? raw.id ?? 0,
    account_id: raw.account_id ?? accountId,
    requires_approval_for_publish: cfg.requires_approval_for_publish ?? false,
    requires_approval_for_spend: cfg.requires_approval_for_spend ?? false,
    spend_threshold: cfg.spend_threshold ?? 0,
    min_approvers: cfg.min_approvers ?? 1,
    auto_approve_after_hours: cfg.auto_approve_after_hours ?? null,
    created_at: raw.created_at ?? new Date().toISOString(),
    updated_at: raw.updated_at ?? null,
  };

  return policy;
}

/**
 * Update approval policy
 */
export async function updateApprovalPolicy(
  accountId: number,
  data: CreateApprovalPolicyRequest
): Promise<ApprovalPolicy> {
  // NOTE: The new API expects /api/approvals/policies/{policyid}?accountid=...
  // We treat accountId as the policy identifier here for backward compatibility.
  const params = new URLSearchParams({ account_id: String(accountId) });

  const raw = await fetchWithCredentials<any>(`/api/approvals/policies/${accountId}?${params.toString()}`, {
    method: 'PUT',
    body: JSON.stringify({ policyconfig: data }),
  });

  const cfg = raw?.policyconfig || data || {};

  const policy: ApprovalPolicy = {
    id: raw.policy_id ?? raw.id ?? accountId,
    account_id: raw.account_id ?? accountId,
    requires_approval_for_publish: cfg.requires_approval_for_publish ?? false,
    requires_approval_for_spend: cfg.requires_approval_for_spend ?? false,
    spend_threshold: cfg.spend_threshold ?? 0,
    min_approvers: cfg.min_approvers ?? 1,
    auto_approve_after_hours: cfg.auto_approve_after_hours ?? null,
    created_at: raw.created_at ?? new Date().toISOString(),
    updated_at: raw.updated_at ?? new Date().toISOString(),
  };

  return policy;
}

/**
 * List approval requests
 */
export async function listApprovalRequests(
  options?: {
    accountId?: number;
    status?: string;
    limit?: number;
    offset?: number;
  }
): Promise<ApprovalRequestListResponse> {
  const params = new URLSearchParams();
  if (options?.accountId) params.append('account_id', String(options.accountId));
  if (options?.status) params.append('status', options.status);
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.offset) params.append('skip', String(options.offset));
  
  const query = params.toString() ? `?${params}` : '';

  // New API: GET /api/approvals?accountid=...&status=...
  const raw = await fetchWithCredentials<any>(`/api/approvals${query}`, {
    method: 'GET',
  });

  const items: ApprovalRequest[] = raw.items || raw.requests || [];
  const total: number = raw.total ?? items.length;
  const pendingCount: number =
    typeof raw.pending_count === 'number'
      ? raw.pending_count
      : items.filter((r: ApprovalRequest) => r.status === 'pending').length;

  const result: ApprovalRequestListResponse = {
    requests: items,
    total,
    pending_count: pendingCount,
  };

  return result;
}

/**
 * Create approval request
 */
export async function createApprovalRequest(
  accountId: number,
  data: CreateApprovalRequestRequest
): Promise<ApprovalRequest> {
  return fetchWithCredentials<ApprovalRequest>('/api/approvals/requests', {
    method: 'POST',
    body: JSON.stringify({ account_id: accountId, ...data }),
  });
}

/**
 * Get approval request with decisions
 */
export async function getApprovalRequest(
  requestId: number
): Promise<ApprovalRequestWithDecisions> {
  return fetchWithCredentials<ApprovalRequestWithDecisions>(`/api/approvals/requests/${requestId}`, {
    method: 'GET',
  });
}

/**
 * Submit approval decision
 */
export async function submitApprovalDecision(
  requestId: number,
  data: ApprovalDecisionRequest
): Promise<ApprovalRequest> {
  return fetchWithCredentials<ApprovalRequest>(`/api/approvals/requests/${requestId}/decide`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Cancel approval request
 */
export async function cancelApprovalRequest(
  requestId: number
): Promise<RBACSuccessResponse> {
  return fetchWithCredentials<RBACSuccessResponse>(`/api/approvals/requests/${requestId}/cancel`, {
    method: 'POST',
  });
}

// ============================================
// RBAC v2.0 - Audit Logs
// ============================================

/**
 * Query audit logs
 */
export async function queryAuditLogs(query: AuditLogQuery): Promise<AuditLogResponse> {
  if (!query.organization_id) {
    throw new Error('organization_id (tenant_id) is required to query audit logs');
  }

  const params = new URLSearchParams();
  params.append('tenant_id', String(query.organization_id));
  if (query.account_id) params.append('accountid', String(query.account_id));
  if (query.user_id) params.append('userid', String(query.user_id));
  if (query.action) params.append('actioncodes', query.action);
  if (query.severity) params.append('severity', query.severity);
  if (query.start_date) params.append('from_date', query.start_date);
  if (query.end_date) params.append('to_date', query.end_date);
  if (query.limit) params.append('limit', String(query.limit));
  if (query.offset) params.append('skip', String(query.offset));

  // New API: GET /api/audit/events
  const raw = await fetchWithCredentials<any>(`/api/audit/events?${params.toString()}`, {
    method: 'GET',
  });

  const items = raw.items || [];
  const total = raw.total ?? items.length;

  const entries: AuditLogEntry[] = items.map((item: any) => ({
    id: item.event_id ?? item.id,
    organization_id: item.organization_id ?? 0,
    user_id: item.actor_userid ?? item.user_id ?? 0,
    user_email: item.actor_email ?? item.user_email ?? '',
    action: item.action,
    resource_type: item.resource_type ?? item.resourceType ?? 'unknown',
    resource_id: item.resource_id ?? item.resourceId,
    severity: item.severity ?? 'info',
    details: item.details ?? { old_value: item.old_value, new_value: item.new_value },
    ip_address: item.ip_address,
    user_agent: item.user_agent,
    created_at: item.timestamp ?? item.created_at,
  } as AuditLogEntry));

  const hasMore =
    typeof raw.skip === 'number' && typeof raw.limit === 'number'
      ? raw.skip + items.length < total
      : false;

  const response: AuditLogResponse = {
    entries,
    total,
    has_more: hasMore,
  };

  return response;
}

/**
 * Get single audit log entry
 */
export async function getAuditLogEntry(entryId: number): Promise<AuditLogEntry> {
  // There is no direct single-entry endpoint in the new spec; fall back to a filtered events query
  const raw = await queryAuditLogs({ organization_id: 0, limit: 1, offset: 0, action: undefined as any });
  const entry = raw.entries.find((e) => e.id === entryId);
  if (!entry) {
    throw new Error('Audit entry not found');
  }
  return entry;
}

/**
 * Export audit logs (returns download URL)
 */
export async function exportAuditLogs(
  query: AuditLogQuery & { format?: 'csv' | 'json' }
): Promise<{ download_url: string }> {
  if (!query.organization_id) {
    throw new Error('organization_id (tenant_id) is required to export audit logs');
  }

  const params = new URLSearchParams();
  params.append('tenant_id', String(query.organization_id));
  if (query.account_id) params.append('accountid', String(query.account_id));
  if (query.start_date) params.append('from_date', query.start_date);
  if (query.end_date) params.append('to_date', query.end_date);
  if (query.format) params.append('format', query.format);

  // New API: GET /api/audit/exports -> returns export_id
  const raw = await fetchWithCredentials<any>(`/api/audit/exports?${params.toString()}`, {
    method: 'GET',
  });

  const exportId = raw.export_id ?? raw.id;
  if (!exportId) {
    throw new Error('Failed to start audit export');
  }

  // Construct download URL for the client to use
  const download_url = `${apiConfig.baseUrls.rbac}/api/audit/exports/${exportId}/download?tenant_id=${query.organization_id}`;

  return { download_url };
}

// ============================================
// v2.0 - Dashboard (Recent Campaigns / Web Analytics)
// ============================================

/**
 * Merged, sorted, paginated campaign list across digital ads, email, and
 * direct mail for one account. Replaces client-side stitching of the three
 * channel services.
 */
export async function getRecentCampaigns(
  accountId: number,
  limit = 10,
  offset = 0,
  days?: number
): Promise<RecentCampaignsResponse> {
  const params = new URLSearchParams({
    account_id: String(accountId),
    limit: String(limit),
    offset: String(offset),
  });
  if (days != null) params.set('days', String(days));
  return fetchWithCredentials<RecentCampaignsResponse>(`/api/dashboard/recent-campaigns?${params.toString()}`, {
    method: 'GET',
  });
}

/**
 * Page views, unique visitors, avg dwell time, bounce rate, and a daily
 * timeline for one account, sourced from PostHog via that account's
 * registered tracking pixels. `days` also sets the prior-period length used
 * for each metric's `change` value.
 */
export async function getWebAnalytics(accountId: number, days = 7): Promise<WebAnalyticsResponse> {
  const params = new URLSearchParams({
    account_id: String(accountId),
    days: String(days),
  });
  return fetchWithCredentials<WebAnalyticsResponse>(`/api/dashboard/web-analytics?${params.toString()}`, {
    method: 'GET',
  });
}

/**
 * Lead path (Sankey) breakdown for one account — funnel nodes/links plus
 * conversion/cart-abandon/checkout rate metrics.
 */
export async function getLeadPath(accountId: number, days = 7): Promise<LeadPathResponse> {
  const params = new URLSearchParams({
    account_id: String(accountId),
    days: String(days),
  });
  return fetchWithCredentials<LeadPathResponse>(`/api/dashboard/lead-path?${params.toString()}`, {
    method: 'GET',
  });
}

// ============================================
// Permission Helpers
// ============================================

/**
 * True if the permission list grants unrestricted access (org owner / admin from RBAC context).
 */
export function hasUnrestrictedPermissions(permissions: string[]): boolean {
  if (!permissions?.length) return false;
  return permissions.includes('*');
}

/**
 * True if a granted permission string satisfies `required` (exact, or prefix.* wildcard).
 */
export function permissionImplies(granted: string, required: string): boolean {
  if (granted === '*' || required === '*') return true;
  if (granted === required) return true;
  if (granted.endsWith('.*')) {
    return matchesPermission(required, granted);
  }
  return false;
}

/**
 * Check if user has specific permission.
 * Supports backend wildcard `*` (all permissions) and `resource.*` patterns from /api/v1/me/context.
 */
export function hasPermission(
  permissions: string[],
  required: string | string[]
): boolean {
  if (!permissions?.length) return false;
  if (hasUnrestrictedPermissions(permissions)) return true;
  const requiredPerms = Array.isArray(required) ? required : [required];
  return requiredPerms.every((perm) =>
    permissions.some((p) => permissionImplies(p, perm))
  );
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(
  permissions: string[],
  required: string[]
): boolean {
  if (!permissions?.length) return false;
  if (hasUnrestrictedPermissions(permissions)) return true;
  return required.some((perm) =>
    permissions.some((p) => permissionImplies(p, perm))
  );
}

/**
 * Check if permission matches with wildcard support
 * e.g., "campaign.*" matches "campaign.view", "campaign.publish"
 */
export function matchesPermission(
  permission: string,
  pattern: string
): boolean {
  if (pattern === '*') return true;
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -2);
    return permission.startsWith(prefix + '.');
  }
  return permission === pattern;
}

/**
 * Check constraints for an action
 */
export function checkConstraints(
  constraints: RoleConstraints | null | undefined,
  context: {
    channel?: string;
    brand_id?: number;
    location_id?: number;
    spend_amount?: number;
  }
): { allowed: boolean; reason?: string } {
  if (!constraints) return { allowed: true };

  if (context.channel && constraints.channels?.length) {
    if (!constraints.channels.includes(context.channel)) {
      return { allowed: false, reason: `Channel "${context.channel}" not allowed` };
    }
  }

  if (context.brand_id && constraints.brands?.length) {
    if (!constraints.brands.includes(context.brand_id)) {
      return { allowed: false, reason: 'Brand not allowed' };
    }
  }

  if (context.location_id && constraints.locations?.length) {
    if (!constraints.locations.includes(context.location_id)) {
      return { allowed: false, reason: 'Location not allowed' };
    }
  }

  if (context.spend_amount !== undefined && constraints.max_spend_daily) {
    if (context.spend_amount > constraints.max_spend_daily) {
      return { allowed: false, reason: 'Exceeds daily spend limit' };
    }
  }

  return { allowed: true };
}

// ============================================
// API Class
// ============================================

/**
 * RBAC API class for advanced usage
 */
export class RBACAPI {
  // Organizations
  createOrganization = createOrganization;
  getMyOrganizations = getMyOrganizations;
  getOrganization = getOrganization;
  updateOrganization = updateOrganization;
  deleteOrganization = deleteOrganization;
  transferOwnership = transferOwnership;
  getOrganizationMembers = getOrganizationMembers;
  inviteMember = inviteMember;
  removeOrganizationMember = removeOrganizationMember;
  updateOrganizationMemberStatus = updateOrganizationMemberStatus;
  getInviteDetails = getInviteDetails;
  acceptInvite = acceptInvite;
  
  // Accounts
  createAccount = createAccount;
  listAccounts = listAccounts;
  getAccount = getAccount;
  updateAccount = updateAccount;
  deleteAccount = deleteAccount;
  grantAccountAccess = grantAccountAccess;
  revokeAccountAccess = revokeAccountAccess;
  listAccountUsers = listAccountUsers;
  getAccountTree = getAccountTree;
  
  // Sub-Accounts
  createSubAccount = createSubAccount;
  listSubAccounts = listSubAccounts;
  getSubAccount = getSubAccount;
  updateSubAccount = updateSubAccount;
  deleteSubAccount = deleteSubAccount;
  grantSubAccountAccess = grantSubAccountAccess;
  revokeSubAccountAccess = revokeSubAccountAccess;
  getSubAccountRoles = getSubAccountRoles;
  assignRoleToSubAccount = assignRoleToSubAccount;
  removeRoleFromSubAccount = removeRoleFromSubAccount;
  
  // Roles
  getSystemRoles = getSystemRoles;
  listRoles = listRoles;
  createRole = createRole;
  getRole = getRole;
  getRoleFull = getRoleFull;
  getAccountRoles = getAccountRoles;
  assignRoleToAccount = assignRoleToAccount;
  removeRoleFromAccount = removeRoleFromAccount;
  updateRole = updateRole;
  deleteRole = deleteRole;
  setRolePermissions = setRolePermissions;
  addPermissionToRole = addPermissionToRole;
  setRoleConstraints = setRoleConstraints;
  
  // Permissions
  listPermissions = listPermissions;
  getPermissionsGrouped = getPermissionsGrouped;
  getPermissionCategories = getPermissionCategories;
  
  // Access Control
  getUserAccessProfile = getUserAccessProfile;
  getUserPermissions = getUserPermissions;
  getUserRoles = getUserRoles;
  assignRole = assignRole;
  assignRoleByCode = assignRoleByCode;
  removeRole = removeRole;
  grantDirectPermissions = grantDirectPermissions;
  revokeDirectPermissions = revokeDirectPermissions;
  getDirectPermissions = getDirectPermissions;
  checkPermission = checkPermission;
  checkPermissionBulk = checkPermissionBulk;
  
  // User Management
  getUserByExternalId = getUserByExternalId;
  getUser = getUser;
  
  // v2.0 - User Setup
  setupUserOrg = setupUserOrg;
  
  // v2.0 - User Context
  getUserContext = getUserContext;
  getFullUserContext = getFullUserContext;
  getMyPermissions = getMyPermissions;
  checkApprovalRequired = checkApprovalRequired;
  
  // v2.0 - Groups
  listGroups = listGroups;
  createGroup = createGroup;
  getGroup = getGroup;
  updateGroup = updateGroup;
  deleteGroup = deleteGroup;
  getGroupMembers = getGroupMembers;
  addGroupMember = addGroupMember;
  bulkAddGroupMembers = bulkAddGroupMembers;
  removeGroupMember = removeGroupMember;
  getGroupRoles = getGroupRoles;
  assignGroupRole = assignGroupRole;
  removeGroupRole = removeGroupRole;
  
  // v2.0 - Object ACL
  listACL = listACL;
  createACL = createACL;
  updateACL = updateACL;
  deleteACL = deleteACL;
  checkObjectAccess = checkObjectAccess;
  
  // v2.0 - Approvals
  getApprovalPolicy = getApprovalPolicy;
  createApprovalPolicy = createApprovalPolicy;
  updateApprovalPolicy = updateApprovalPolicy;
  listApprovalRequests = listApprovalRequests;
  createApprovalRequest = createApprovalRequest;
  getApprovalRequest = getApprovalRequest;
  submitApprovalDecision = submitApprovalDecision;
  cancelApprovalRequest = cancelApprovalRequest;
  
  // v2.0 - Audit Logs
  queryAuditLogs = queryAuditLogs;
  getAuditLogEntry = getAuditLogEntry;
  exportAuditLogs = exportAuditLogs;

  // v2.0 - Dashboard
  getRecentCampaigns = getRecentCampaigns;
  getWebAnalytics = getWebAnalytics;
  getLeadPath = getLeadPath;

  // Helpers
  hasPermission = hasPermission;
  hasAnyPermission = hasAnyPermission;
  matchesPermission = matchesPermission;
  checkConstraints = checkConstraints;
}

export const rbacApi = new RBACAPI();
export default rbacApi;


